import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { AppLayout } from '@/components/AppLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { GitBranch, Loader2, AlertCircle, FileArchive, MoreHorizontal, Copy, Check, Sparkles, Users, Folder } from 'lucide-react';
import { useProjectsManager } from '@/hooks/useProjectsManager';
import { useToast } from '@/hooks/useToast';
import { ZipImportDialog } from '@/components/ZipImportDialog';
import { useSeoMeta } from '@unhead/react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useNostr } from '@nostrify/react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { cn, generateVividGradient } from '@/lib/utils';

interface RepositoryEvent extends NostrEvent {
  kind: 30617;
}

export default function Clone() {
  const { t } = useTranslation();
  const [repoUrl, setRepoUrl] = useState('');
  const [isCloning, setIsCloning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectsManager = useProjectsManager();
  const { toast } = useToast();
  const autoCloneInitiatedRef = useRef(false);
  const [isZipDialogOpen, setIsZipDialogOpen] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  useSeoMeta({
    title: `${t('importRepository')} - AfterHours`,
    description: t('cloneGitRepository'),
  });

  // Fetch user's repositories (My Apps)
  const { data: myRepos, isLoading: isLoadingMyRepos } = useQuery({
    queryKey: ['my-repos', user?.pubkey],
    queryFn: async ({ signal }) => {
      if (!user?.pubkey) return [];

      console.log('[Clone] Querying my repos for pubkey:', user.pubkey);
      const events = await nostr.query(
        [{ kinds: [30617], authors: [user.pubkey], limit: 100 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]) }
      );

      console.log('[Clone] Found', events.length, 'repos for user');
      return events as RepositoryEvent[];
    },
    enabled: !!user?.pubkey,
  });

  // Fetch user's following list
  const { data: following } = useQuery({
    queryKey: ['following', user?.pubkey],
    queryFn: async ({ signal }) => {
      if (!user?.pubkey) return [];

      console.log('[Clone] Querying following list for pubkey:', user.pubkey);
      const events = await nostr.query(
        [{ kinds: [3], authors: [user.pubkey], limit: 1 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]) }
      );

      const followList = events[0];
      if (!followList) {
        console.log('[Clone] No follow list found');
        return [];
      }

      // Extract pubkeys from p tags
      const followedPubkeys = followList.tags
        .filter(tag => tag[0] === 'p' && tag[1])
        .map(tag => tag[1]);

      console.log('[Clone] Following', followedPubkeys.length, 'people');
      return followedPubkeys;
    },
    enabled: !!user?.pubkey,
  });

  // Fetch repositories from following
  const { data: followingRepos, isLoading: isLoadingFollowingRepos } = useQuery({
    queryKey: ['following-repos', following],
    queryFn: async ({ signal }) => {
      if (!following || following.length === 0) return [];

      console.log('[Clone] Querying repos from', following.length, 'followed users');
      const events = await nostr.query(
        [{ kinds: [30617], authors: following, limit: 200 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]) }
      );

      console.log('[Clone] Found', events.length, 'repos from following');
      return events as RepositoryEvent[];
    },
    enabled: !!following && following.length > 0,
  });

  const extractRepoName = (url: string): string => {
    try {
      const cleanUrl = url.trim();

      // Handle Nostr clone URIs
      if (cleanUrl.startsWith('nostr://')) {
        const path = cleanUrl.slice(8); // Remove 'nostr://' prefix
        const parts = path.split('/');

        // Return the d-tag (last part) as the repo name
        if (parts.length >= 2) {
          return parts[parts.length - 1] || 'nostr-repo';
        }
        return 'nostr-repo';
      }

      // Extract from GitHub URLs, GitLab URLs, etc.
      const match = cleanUrl.match(/\/([^/]+?)(?:\.git)?(?:\/)?$/);
      if (match && match[1]) {
        return match[1];
      }

      // Fallback: use the last part of the URL
      const parts = cleanUrl.split('/').filter(Boolean);
      const lastPart = parts[parts.length - 1];
      return lastPart.replace('.git', '') || 'imported-repo';
    } catch {
      return 'imported-repo';
    }
  };

  const validateGitUrl = useCallback((url: string): boolean => {
    if (!url.trim()) return false;

    // Check if it's a Nostr clone URI
    if (url.startsWith('nostr://')) {
      return true; // Git class will handle validation
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      return false;
    }

    // Check if it looks like a git repository URL
    const gitUrlPattern = /^https?:\/\/.*\.git$|^https?:\/\/github\.com\/.*\/.*$|^https?:\/\/gitlab\.com\/.*\/.*$/i;
    return gitUrlPattern.test(url) || url.includes('github.com') || url.includes('gitlab.com');
  }, []);

  const handleClone = useCallback(async (url?: string) => {
    const targetUrl = url || repoUrl;

    if (!targetUrl.trim()) {
      setError(t('pleaseEnterRepositoryUrl'));
      return;
    }

    if (!validateGitUrl(targetUrl)) {
      setError(t('pleaseEnterValidGitUrl'));
      return;
    }

    setIsCloning(true);
    setError(null);

    try {
      await projectsManager.init();

      // Extract repository name
      const repoName = extractRepoName(targetUrl);

      // Clone the repository (Git class handles both regular Git URLs and Nostr URIs)
      const project = await projectsManager.cloneProject(repoName, targetUrl.trim());

      // Determine success message based on URL type
      const isNostrUri = targetUrl.startsWith('nostr://');
      const successTitle = isNostrUri
        ? t('nostrRepositoryImportedSuccessfully')
        : t('repositoryImportedSuccessfully');
      const successDescription = isNostrUri
        ? t('repositoryClonedFromNostr', { repoName })
        : t('repositoryClonedReady', { repoName });

      toast({
        title: successTitle,
        description: successDescription,
      });

      // Navigate to the new project with build parameter
      navigate(`/project/${project.id}?build`);
    } catch (error) {
      console.error('Failed to clone repository:', error);

      let errorMessage = t('failedToImportRepository');
      if (error instanceof Error) {
        if (error.message.includes('Repository not found on Nostr network')) {
          errorMessage = t('repositoryNotFoundOnNostr');
        } else if (error.message.includes('No clone URLs found')) {
          errorMessage = t('noCloneUrlsFound');
        } else if (error.message.includes('All clone attempts failed')) {
          errorMessage = t('allCloneAttemptsFailed');
        } else if (error.message.includes('404') || error.message.includes('not found')) {
          errorMessage = t('repositoryNotFound');
        } else if (error.message.includes('403') || error.message.includes('forbidden')) {
          errorMessage = t('accessDenied');
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorMessage = t('networkError');
        } else {
          errorMessage = error.message;
        }
      }

      setError(errorMessage);

      toast({
        title: t('failedToImportRepository'),
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsCloning(false);
    }
  }, [navigate, projectsManager, repoUrl, t, toast, validateGitUrl]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isCloning) {
      handleClone();
    }
  };

  // Initialize repoUrl from URL parameters and auto-import if URL is provided
  useEffect(() => {
    const urlParam = searchParams.get('url');
    if (urlParam && !autoCloneInitiatedRef.current) {
      const decodedUrl = decodeURIComponent(urlParam);
      setRepoUrl(decodedUrl);
      autoCloneInitiatedRef.current = true;

      // Automatically start the clone process with the URL directly
      handleClone(decodedUrl);
    }
  }, [handleClone, searchParams]);

  const handleImportZip = async (file: File, overwrite = false, projectId?: string) => {
    try {
      // Import project from zip file
      const project = await projectsManager.importProjectFromZip(file, projectId, overwrite);

      // Show success toast
      toast({
        title: overwrite ? "Project Overwritten Successfully" : "Project Imported Successfully",
        description: `"${project.name}" has been ${overwrite ? 'overwritten' : 'imported'} and is ready to use.`,
      });

      // Navigate to the imported project
      navigate(`/project/${project.id}`);
    } catch (error) {
      console.error('Failed to import project:', error);
      throw error; // Re-throw to let the dialog handle the error display
    }
  };

  const getRepoName = (repo: RepositoryEvent): string => {
    const nameTag = repo.tags.find(tag => tag[0] === 'name');
    const dTag = repo.tags.find(tag => tag[0] === 'd');
    return nameTag?.[1] || dTag?.[1] || 'Unnamed Repository';
  };

  const getRepoDescription = (repo: RepositoryEvent): string => {
    const descTag = repo.tags.find(tag => tag[0] === 'description');
    return descTag?.[1] || '';
  };

  const getCloneUrl = (repo: RepositoryEvent): string => {
    const cloneTag = repo.tags.find(tag => tag[0] === 'clone');
    if (cloneTag?.[1]) return cloneTag[1];

    // Fallback to nostr:// URL
    const dTag = repo.tags.find(tag => tag[0] === 'd');
    if (dTag?.[1]) {
      return `nostr://${repo.pubkey}/${dTag[1]}`;
    }

    return '';
  };

  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      toast({
        title: 'URL Copied',
        description: 'Repository URL copied to clipboard',
      });
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
      toast({
        title: 'Failed to Copy',
        description: 'Could not copy URL to clipboard',
        variant: 'destructive',
      });
    }
  };

  const handleCloneRepo = async (repo: RepositoryEvent) => {
    const url = getCloneUrl(repo);
    if (url) {
      await handleClone(url);
    }
  };

  const renderRepoCard = (repo: RepositoryEvent, index: number) => {
    const name = getRepoName(repo);
    const description = getRepoDescription(repo);
    const cloneUrl = getCloneUrl(repo);
    const isCopied = copiedUrl === cloneUrl;

    // Generate a deterministic vivid gradient based on the repo id (not using useMemo since this is not a component)
    const gradientStyle = {
      background: generateVividGradient(repo.id),
    };

    return (
      <motion.div
        key={repo.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
      >
        <div className="group relative rounded-2xl border border-border/60 bg-gradient-to-b from-card to-card/60 overflow-hidden shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/10">
          {/* Gradient header with folder icon */}
          <div className="relative aspect-[16/10] flex items-center justify-center" style={gradientStyle}>
            <Folder className="h-16 w-16 text-white/50" />
          </div>

          <div className="relative p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1 min-w-0">
                <h3 className="font-semibold text-base md:text-lg tracking-tight truncate group-hover:text-primary transition-colors">
                  {name}
                </h3>
                {description && (
                  <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                    {description}
                  </p>
                )}
              </div>
              <div className="flex-shrink-0 rounded-xl bg-muted/70 p-2">
                <GitBranch className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Button
                size="sm"
                onClick={() => handleCloneRepo(repo)}
                disabled={isCloning || !cloneUrl}
                className="flex-1 h-9 rounded-xl text-sm shadow-sm"
              >
                <GitBranch className="h-3.5 w-3.5 mr-2" />
                Clone
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleCopyUrl(cloneUrl)}
                disabled={!cloneUrl}
                className={cn(
                  "h-9 w-9 rounded-xl border-border/60 hover:border-primary/40 hover:bg-primary/5",
                  isCopied && "border-green-500/60 bg-green-500/10 text-green-600"
                )}
              >
                {isCopied ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const renderRepoSkeleton = (index: number) => (
    <motion.div
      key={index}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="rounded-2xl border border-border/60 bg-gradient-to-b from-card to-card/60 overflow-hidden"
    >
      <div className="aspect-[16/10] bg-muted/60 flex items-center justify-center">
        <Folder className="h-16 w-16 text-muted-foreground/30" />
      </div>
      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-2 flex-1">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
          <Skeleton className="h-8 w-8 rounded-xl" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-9 flex-1 rounded-xl" />
          <Skeleton className="h-9 w-9 rounded-xl" />
        </div>
      </div>
    </motion.div>
  );

  return (
    <AppLayout title={t('importRepository')}>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-5">
            <GitBranch className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-3 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
            {t('importRepository')}
          </h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            {t('cloneGitRepository')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05 }}
          className="space-y-3 mb-10"
        >
          <div className="relative">
            <Input
              id="repo-url"
              type="text"
              placeholder="https://github.com/username/repository.git"
              value={repoUrl}
              onChange={(e) => {
                setRepoUrl(e.target.value);
                setError(null);
              }}
              onKeyDown={handleKeyDown}
              disabled={isCloning}
              className="h-12 rounded-xl bg-background border-border/60 focus:border-primary/50 focus:ring-primary/20 pl-4 pr-4 text-base"
            />
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2.5 p-3.5 bg-destructive/10 border border-destructive/20 rounded-xl"
            >
              <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
              <p className="text-sm text-destructive">{error}</p>
            </motion.div>
          )}

          <Button
            onClick={() => handleClone()}
            disabled={!repoUrl.trim() || isCloning}
            className="w-full h-12 rounded-xl font-medium text-base shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/25 transition-all duration-200"
            size="lg"
          >
            {isCloning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('cloningRepository')}
              </>
            ) : (
              <>
                <GitBranch className="mr-2 h-4 w-4" />
                {t('importRepository')}
              </>
            )}
          </Button>
        </motion.div>

        {/* Tabs for My Apps and Following */}
        {user && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Tabs defaultValue="my-apps" className="w-full">
              <TabsList className="grid w-full grid-cols-2 h-12 p-1 bg-muted/50 backdrop-blur-sm rounded-2xl border border-border/40 mb-8">
                <TabsTrigger
                  value="my-apps"
                  className="gap-2.5 h-10 rounded-xl data-[state=active]:bg-background data-[state=active]:shadow-md data-[state=active]:border-border/50 transition-all duration-200"
                >
                  <Sparkles className="h-4 w-4" />
                  <span className="font-medium">My Apps</span>
                  {myRepos && myRepos.length > 0 && (
                    <Badge className="rounded-full px-2 py-0 text-xs bg-primary/15 text-primary border-0 font-semibold">
                      {myRepos.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value="following"
                  className="gap-2.5 h-10 rounded-xl data-[state=active]:bg-background data-[state=active]:shadow-md data-[state=active]:border-border/50 transition-all duration-200"
                >
                  <Users className="h-4 w-4" />
                  <span className="font-medium">Following</span>
                  {followingRepos && followingRepos.length > 0 && (
                    <Badge className="rounded-full px-2 py-0 text-xs bg-primary/15 text-primary border-0 font-semibold">
                      {followingRepos.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="my-apps" className="mt-0">
                {isLoadingMyRepos ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {[...Array(4)].map((_, i) => renderRepoSkeleton(i))}
                  </div>
                ) : myRepos && myRepos.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {myRepos.map((repo, index) => renderRepoCard(repo, index))}
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    className="rounded-2xl border-2 border-dashed border-border/50 bg-gradient-to-b from-muted/30 to-transparent p-12 text-center"
                  >
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-muted/50 mb-5">
                      <Sparkles className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground font-medium mb-1">
                      No repositories yet
                    </p>
                    <p className="text-sm text-muted-foreground/70">
                      Publish your first repository to see it here
                    </p>
                  </motion.div>
                )}
              </TabsContent>

              <TabsContent value="following" className="mt-0">
                {isLoadingFollowingRepos ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {[...Array(4)].map((_, i) => renderRepoSkeleton(i))}
                  </div>
                ) : followingRepos && followingRepos.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {followingRepos.map((repo, index) => renderRepoCard(repo, index))}
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    className="rounded-2xl border-2 border-dashed border-border/50 bg-gradient-to-b from-muted/30 to-transparent p-12 text-center"
                  >
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-muted/50 mb-5">
                      <Users className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground font-medium mb-1">
                      {following && following.length > 0
                        ? "No repositories from your network"
                        : "Follow people to discover apps"}
                    </p>
                    <p className="text-sm text-muted-foreground/70">
                      {following && following.length > 0
                        ? "None of the people you follow have published yet"
                        : "Repositories from people you follow will appear here"}
                    </p>
                  </motion.div>
                )}
              </TabsContent>
            </Tabs>
          </motion.div>
        )}

        {/* Import ZIP File Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="mt-10 text-center"
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-xl gap-2 px-4"
              >
                <MoreHorizontal className="h-4 w-4" />
                <span className="text-sm">More options</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="rounded-xl">
              <DropdownMenuItem
                className="flex items-center gap-2.5 rounded-lg cursor-pointer"
                onClick={() => setIsZipDialogOpen(true)}
              >
                <FileArchive className="h-4 w-4" />
                Import ZIP File
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </motion.div>
      </div>

      {/* ZIP Import Dialog */}
      <ZipImportDialog
        onImport={handleImportZip}
        open={isZipDialogOpen}
        onOpenChange={setIsZipDialogOpen}
      />
    </AppLayout>
  );
}